/**
 * Hikâye detayının SAF birleştirme mantığı — ağ ve supabase-js bağımlılığı yok.
 *
 * İki yerden kullanılır ve bu yüzden ayrı dosyada:
 *  - SPA: src/lib/api/content.ts → fetchStory (supabase-js ile okur, burada birleştirir)
 *  - Kenar: functions/s/[id].ts (REST ile okur, burada birleştirir, sonucu HTML'e
 *    gömer ve React Query önbelleğine tohum olarak verir)
 * Aynı fonksiyon iki tarafta da çalıştığı için tohumun şekli SPA'nın beklediği
 * şekilden asla kaymaz; kaysaydı React sunucunun gömdüğü veriyi yanlış çizerdi.
 */
import type { Category, ContentCard, ContentDetail, FeedItem, Source, Subcategory } from './types';

export const FEED_COLUMNS =
  'id,title,author,url,source_id,category_id,subcategory_id,summary_word_count,first_seen_at,published_at,cover_image_url,cluster_id,is_synthesis';
export const DETAIL_COLUMNS = `${FEED_COLUMNS},word_count,media_url,audio_url,k_what,k_who,k_where,k_when,k_why,k_how`;
export const CARD_COLUMNS = 'id,content_id,idx,kind,heading,body,lang,style';

export type SourceLite = Pick<Source, 'id' | 'title' | 'site_url' | 'logo_url' | 'kind'>;
export type StoryMember = FeedItem & { source: SourceLite | null };
export type StoryTag = {
  categoryId: string;
  categorySlug: string;
  categoryName: string;
  subSlug: string | null;
  subName: string | null;
  primary: boolean;
};

export type StoryDetail = {
  content: ContentDetail;
  cards: ContentCard[];
  cardLang: string;
  category: Category | null;
  source: SourceLite | null;
  members: StoryMember[];
  tags: StoryTag[];
};

export type TagRow = { category_id: string; subcategory_id: string | null; kind: string };

/**
 * İstenen dil → İngilizce. Web Türkçe kart göstermez (ürün kararı); yalnız
 * Türkçe kartı olan eski içerik için boş döner ve çağıran İngilizce başlığa düşer.
 */
export function pickLangCards(all: ContentCard[], lang: string): ContentCard[] {
  const expert = all.filter((c) => c.style === 'expert');
  const pool = (expert.length > 0 ? expert : all).filter((c) => c.lang !== 'tr');
  for (const l of [lang, 'en']) {
    const hit = pool.filter((c) => c.lang === l);
    if (hit.length > 0) return hit.sort((a, b) => a.idx - b.idx);
  }
  return pool.sort((a, b) => a.idx - b.idx);
}

export function assembleStory(
  raw: {
    content: ContentDetail;
    cards: ContentCard[];
    categories: Category[];
    subcategories: Subcategory[];
    sources: Map<string, SourceLite>;
    tagRows: TagRow[];
    /** Yalnız sentez hikâyelerde: kümedeki özgün haberler. */
    members: FeedItem[];
  },
  lang: string
): StoryDetail {
  const { content, sources } = raw;
  const cards = pickLangCards(raw.cards, lang);
  const catById = new Map(raw.categories.map((c) => [c.id, c]));
  const subById = new Map(raw.subcategories.map((s) => [s.id, s]));

  const tags = raw.tagRows
    .map((t) => {
      const c = catById.get(t.category_id);
      if (!c) return null;
      const s = t.subcategory_id ? subById.get(t.subcategory_id) : undefined;
      return {
        categoryId: c.id,
        categorySlug: c.slug,
        categoryName: c.name,
        subSlug: s?.slug ?? null,
        subName: s?.name ?? null,
        primary: t.kind === 'primary',
      };
    })
    .filter((t): t is StoryTag => !!t)
    .sort((a, b) => Number(b.primary) - Number(a.primary));

  return {
    content,
    cards,
    cardLang: cards[0]?.lang ?? lang,
    category: content.category_id ? catById.get(content.category_id) ?? null : null,
    source: sources.get(content.source_id) ?? null,
    members: raw.members.map((m) => ({ ...m, source: sources.get(m.source_id) ?? null })),
    tags,
  };
}
